import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Menu, X } from "lucide-react";
import { API_URL, Button, BarreNavigation } from "./Dashboard";

const Parametres = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentMdp, setCurrentMdp] = useState("");
  const [nouveauMdp, setNouveauMdp] = useState("");
  const [confirmMdp, setConfirmMdp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/");
      return;
    }
    setUser(storedUser);
  }, [navigate]);

  if (!user) return null;

  const identifiant = user.identifiant;

  const resetMessage = () => setMessage(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessage();

    if (!currentMdp || !nouveauMdp || !confirmMdp) {
      setMessage({ type: "error", text: "Remplissez tous les champs." });
      return;
    }
    if (nouveauMdp.length < 8) {
      setMessage({ type: "error", text: "Le nouveau mot de passe doit contenir au moins 8 caractères." });
      return;
    }
    if (nouveauMdp !== confirmMdp) {
      setMessage({ type: "error", text: "La confirmation ne correspond pas au nouveau mot de passe." });
      return;
    }
    if (nouveauMdp === currentMdp) {
      setMessage({ type: "error", text: "Le nouveau mot de passe doit être différent du mot de passe actuel." });
      return;
    }
    if (!identifiant) {
      setMessage({ type: "error", text: "Impossible de retrouver l'identifiant utilisateur. Déconnectez-vous et reconnectez-vous." });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/reinitialiserMdp.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifiant,
          currentMdp,
          nouveauMdp,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setMessage({ type: "success", text: "Mot de passe modifié avec succès." });
        setCurrentMdp("");
        setNouveauMdp("");
        setConfirmMdp("");
      } else {
        setMessage({ type: "error", text: data.error || "Erreur serveur inconnue." });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Erreur réseau. Vérifiez que le serveur est démarré." });
    } finally {
      setLoading(false);
    }
  };

  const seDeconnecter = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden">
        
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
         <BarreNavigation 
            user={user} 
            deconnexion={seDeconnecter} 
            onNavigate={() => setIsMobileMenuOpen(false)}
         />
         <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 lg:hidden text-gray-500"
        >
            <X size={24} />
        </button>
      </div>

      <main className="flex-1 p-4 lg:p-8 flex flex-col items-center w-full">
        <header className="flex items-center justify-between w-full mb-6 gap-4">
          <div className="flex items-center gap-3">
             <button 
                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setIsMobileMenuOpen(true)}
            >
                <Menu size={24} />
            </button>
            <h1 className="text-2xl font-bold text-gris-sombre">Paramètres</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={seDeconnecter} title="Se déconnecter">
                <span className="hidden sm:inline">Se déconnecter</span>
                <LucideIcons.LogOut size={16} className="sm:hidden" />
            </Button>
          </div>
        </header>

        {/* Formulaire centré verticalement */}
        <section className="max-w-3xl w-full bg-white rounded-2xl shadow p-4 md:p-6 mt-auto mb-auto">
          <h2 className="text-lg font-semibold mb-4">Changer le mot de passe</h2>

          {message && (
            <div
              role="alert"
              className={`mb-4 p-3 rounded ${
                message.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mot de passe actuel</label>
              <input
                type="password"
                value={currentMdp}
                onChange={(e) => setCurrentMdp(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                required
                autoComplete="current-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nouveau mot de passe</label>
              <input
                type="password"
                value={nouveauMdp}
                onChange={(e) => setNouveauMdp(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">8 caractères minimum. Utilisez un mot de passe différent pour chaque service, suffisamment long et complexe.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                value={confirmMdp}
                onChange={(e) => setConfirmMdp(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                required
                autoComplete="new-password"
              />
            </div>

            <div className="flex flex-wrap justify-end gap-3 pt-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => { setCurrentMdp(""); setNouveauMdp(""); setConfirmMdp(""); }}
                className="w-full sm:w-auto" // Pleine largeur sur mobile
              >
                Réinitialiser les champs
              </Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                {loading ? (
                  <>
                    <LucideIcons.Loader2 className="animate-spin inline mr-2" size={16} /> En cours...
                  </>
                ) : "Enregistrer le nouveau mot de passe"}
              </Button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

export default Parametres;